import React, { useState, useEffect } from "react";
// import { Route, Routes } from "react-router-dom"; // Import Router components
import { Tabs, Tab } from "@mui/material";

/*eslint-disable*/

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

import { FiInfo, FiCheckSquare } from "react-icons/fi";

import OfflineMessage from "../../components/offline_message";

ChartJS.register(
    CategoryScale,
    LinearScale,
    LineElement,
    BarElement,
    ArcElement,
    PointElement,
    Title,
    Tooltip,
    Legend
);

export const options = {
    responsive: true,
    plugins: {
        legend: {
            display: false,
            position: "top",
        },
        title: {
            display: false,
            text: "Chart.js Bar Chart",
        },
    },
};

import { FormattedMessage, IntlProvider } from "react-intl";

import messages_en from "./translations/en.json";
import messages_pt from "./translations/pt.json";
import messages_fr from "./translations/fr.json";
import SmallStatisticsBox from "../../components/Charts/SmallStatisticsBox";

const messages = {
    en: messages_en,
    pt: messages_pt,
    fr: messages_fr,
};

// Utilities and configurations
import FastAPIClient from "../../client";
import config from "../../config";
import Reports from "./subpages/reports";
import Dashboard from "./subpages/dashboard";
import SalesReps from "./subpages/salesreps";
import Analysis from "./subpages/analysis";
import Usage from "./subpages/usage";

const client = new FastAPIClient(config);

const useNetworkStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isSlowConnection, setIsSlowConnection] = useState(false);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        const handleConnectionChange = () => {
            const connection =
                navigator.connection ||
                navigator.mozConnection ||
                navigator.webkitConnection;
            if (connection) {
                setIsSlowConnection(
                    connection.effectiveType === "2g" ||
                        connection.effectiveType === "3g" ||
                        connection.downlink < 1.5
                );
            }
        };

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        if (navigator.connection) {
            handleConnectionChange();
            navigator.connection.addEventListener(
                "change",
                handleConnectionChange
            );
        }

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);

            if (navigator.connection) {
                navigator.connection.removeEventListener(
                    "change",
                    handleConnectionChange
                );
            }
        };
    }, []);

    return { isOnline, isSlowConnection };
};

const ManagementDashboard = () => {
    const { isOnline, isSlowConnection } = useNetworkStatus();

    const [locale, setLocale] = useState();
    const [userRole, setUserRole] = useState(null);

    useEffect(() => {
        if (isOnline && !isSlowConnection) {
            setLocale(localStorage.getItem("language"));
            const keyUser = localStorage.getItem("user");
            if (keyUser) {
                try {
                    const user = JSON.parse(keyUser);
                    setUserRole(user.user_role_id);
                } catch (e) {
                    console.error(
                        "Failed to parse key_user from local storage",
                        e
                    );
                    setUserRole(null); // Set to null if parsing fails
                }
            } else {
                setUserRole(null); // Set to null if key_user is not found
            }
        }
    }, [isOnline, isSlowConnection]);

    const [currentPath, setCurrentPath] = useState(window.location.pathname);

    // Function to handle navigation and URL update
    const navigate = (path) => {
        window.history.pushState({}, "", path);
        setCurrentPath(path); // Trigger re-render by updating the state
    };

    // Listen for popstate events (back/forward buttons)
    useEffect(() => {
        const handlePopState = () => {
            setCurrentPath(window.location.pathname);
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    const currentDate = new Date();
    // Use Intl.DateTimeFormat to get the date parts in French
    const formatter = new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
    const parts = formatter.formatToParts(currentDate);

    let day = "";
    let month = "";
    let year = "";

    for (const part of parts) {
        if (part.type === "day") {
            day = part.value;
        } else if (part.type === "month") {
            month = part.value;
        } else if (part.type === "year") {
            year = part.value;
        }
    }

    // Capitalize the first letter of the month
    const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);

    // Combine them in the desired format
    const formattedDate = `${day} ${capitalizedMonth} ${year}`;

    const [successMessage, setSuccessMessage] = useState(""); // Track success message
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(""), 3000); // Hide message after 3 seconds
            return () => clearTimeout(timer); // Cleanup on unmount
        }
    }, [successMessage]);

    const [errorMessage, setErrorMessage] = useState(""); // Track success message
    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => setErrorMessage(""), 3000); // Hide message after 3 seconds
            return () => clearTimeout(timer); // Cleanup on unmount
        }
    }, [errorMessage]);

    // Conditionally render content based on the current path
    let content;
    if (currentPath === "/management/dashboard") {
        content = <Dashboard />;
    } else if (currentPath === "/management/reports") {
        content = <Reports />;
    } else if (currentPath === "/management/salesreps") {
        content = (
            <SalesReps
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
            />
        );
    } else if (currentPath === "/management/analysis") {
        content = <Analysis />;
    } else if (currentPath === "/management/usage") {
        content = <Usage />;
    } else {
        content = <div>404 Not Found</div>;
    }

    // Define allowed roles for the MYWAY tab
    const allowedRoles = ["R-01", "R-02"];
    const showMyWayTab = userRole !== null && allowedRoles.includes(userRole);

    if (!isOnline) {
        return <OfflineMessage />;
    }
    if (isOnline) {
        return (
            <div className="p-5">
                <IntlProvider locale={locale} messages={messages[locale]}>
                    <div className=" text-left flex flex-row items-center">
                        <h1 className="text-xl text-black">
                            <FormattedMessage
                                id="performance.my_statistics"
                                values={{ number: 1 }}
                            />
                        </h1>
                        <h1 className=" text-sm text-gray-400 ml-5 ">
                            <FormattedMessage
                                id="performance.last_updated"
                                values={{ number: 1 }}
                            />
                            {formattedDate}
                        </h1>
                    </div>
                    <Tabs
                        value={currentPath}
                        onChange={(e, newValue) => navigate(newValue)}
                        // indicatorColor="primary"
                        // textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        // className="mb-2"
                    >
                        <Tab
                            label="Général"
                            value="/management/dashboard"
                            sx={{
                                alignItems: "flex-start",
                                textAlign: "left",
                                fontSize: "0.875rem",
                                paddingLeft: 0,
                                paddingBottom: 0,
                            }}
                        />

                        <Tab
                            label="Activité"
                            value="/management/salesreps"
                            sx={{
                                alignItems: "flex-start",
                                textAlign: "left",
                                fontSize: "0.875rem",
                                paddingLeft: 0,
                                paddingBottom: 0,
                            }}
                        />
                        <Tab
                            label="Analyses"
                            value="/management/analysis"
                            sx={{
                                alignItems: "flex-start",
                                textAlign: "left",
                                fontSize: "0.875rem",
                                paddingLeft: 0,
                                paddingBottom: 0,
                            }}
                        />
                        {showMyWayTab && (
                            <Tab
                                label="MYWAY"
                                value="/management/usage"
                                sx={{
                                    alignItems: "flex-start",
                                    textAlign: "left",
                                    fontSize: "0.875rem",
                                    paddingLeft: 0,
                                    paddingBottom: 0,
                                }}
                            />
                        )}
                    </Tabs>

                    <div className="mt-4">{content}</div>
                    {successMessage && (
                        <div className="fixed bottom-0 left-0 right-0 bg-green-500 text-white text-center py-2 z-50 mx-auto w-1/5 rounded-t-lg">
                            {successMessage}
                        </div>
                    )}
                    {errorMessage && (
                        <div className="fixed bottom-0 left-0 right-0 bg-rose-500 text-white text-center py-2 z-50 mx-auto w-1/5 rounded-t-lg">
                            {errorMessage}
                        </div>
                    )}
                </IntlProvider>
            </div>
        );
    }
};

export default ManagementDashboard;
